import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { SubmissionValuesData } from 'src/functions/hubspot/hubspot.service';
import { getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';

interface ProfitSalesHubspotFormContextData {
  isSubmitted: boolean;
  userName: string;
  userEmail: string;
  wasJustSubmitted: boolean;
  setIsSubmitted(value: boolean, submissionValues: SubmissionValuesData): void;
  setUserName(value: string): void;
  setUserEmail(value: string): void;
}

const ProfitSalesHubspotFormContext = createContext<ProfitSalesHubspotFormContextData>(
  {} as ProfitSalesHubspotFormContextData
);

interface ProfitSalesHubspotFormProviderProps extends PropsWithChildren {
  storageKey?: string;
}

const ProfitSalesHubspotFormProvider: React.FC<ProfitSalesHubspotFormProviderProps> = ({
  children,
  storageKey = 'profitSalesPage',
}) => {
  // hasJustSubmitted is non-persistent pair to isSubmitted while provider is mounted
  const [wasJustSubmitted, setHasJustSubmitted] = useState<boolean>(false);
  const [isSubmitted, _setIsSubmitted] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>(null);
  const [userEmail, setUserEmail] = useState<string>(null);

  useEffect(() => {
    const submission = getLocalStorage(`${storageKey}:submitted`);
    const userName = getLocalStorage(`${storageKey}:userName`);
    const userEmail = getLocalStorage(`${storageKey}:userEmail`);

    if (submission === 'true') {
      _setIsSubmitted(true);
      setUserName(userName);
      setUserEmail(userEmail);
    }
  }, []);

  const setIsSubmitted = (value: boolean, submissionValues: SubmissionValuesData) => {
    _setIsSubmitted(value);
    setHasJustSubmitted(true);

    if (submissionValues) {
      setLocalStorage(`${storageKey}:userName`, submissionValues.firstname);
      setLocalStorage(`${storageKey}:userEmail`, submissionValues.email);
      setLocalStorage(`${storageKey}:submitted`, 'true');
    }
  };

  return (
    <ProfitSalesHubspotFormContext.Provider
      value={{
        isSubmitted,
        wasJustSubmitted,
        setIsSubmitted,
        userName,
        setUserName,
        userEmail,
        setUserEmail,
      }}
    >
      {children}
    </ProfitSalesHubspotFormContext.Provider>
  );
};

const useProfitSalesHubspotForm = (): ProfitSalesHubspotFormContextData => {
  const context = useContext(ProfitSalesHubspotFormContext);

  if (!context) {
    throw new Error('useProfitSalesHubspotForm must be used within a ProfitSalesHubspotFormProvider');
  }

  return context;
};

export { useProfitSalesHubspotForm, ProfitSalesHubspotFormProvider };
